import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null, 'available', 'taken'

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError(''); // Clear error when user types

    // Check username availability as user types (with debounce)
    if (e.target.name === 'username') {
      setUsernameStatus(null);
      if (e.target.value.length >= 3) {
        clearTimeout(window.usernameTimeout);
        window.usernameTimeout = setTimeout(() => {
          checkUsernameAvailability(e.target.value);
        }, 500);
      }
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (username.length < 3) return;
    
    setUsernameChecking(true);
    try {
      const response = await authService.checkUsername(username);
      setUsernameStatus(response.available ? 'available' : 'taken');
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setUsernameChecking(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Le nom du club est requis');
      return false;
    }
    if (!formData.username.trim()) {
      setError('Le nom d\'utilisateur est requis');
      return false;
    }
    if (formData.username.length < 3) {
      setError('Le nom d\'utilisateur doit contenir au moins 3 caractères');
      return false;
    }
    if (usernameStatus === 'taken') {
      setError('Ce nom d\'utilisateur n\'est pas disponible');
      return false;
    }
    if (!formData.password) {
      setError('Le mot de passe est requis');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      const clubData = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        password: formData.password
      };
      
      const response = await authService.register(clubData);
      
      if (response.success) {
        login(response.club, response.token);
        toast({
          title: "Compte créé avec succès",
          description: `Bienvenue ${response.club.name} !`,
        });
        
        // New clubs always go to dashboard (not admin)
        navigate('/dashboard');
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Erreur lors de la création du compte. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">PV Manager</h1>
              <p className="text-sm text-gray-600">Créer un club</p>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <Card className="border border-green-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-green-700">Créer votre club</CardTitle>
            <p className="text-gray-600 text-sm">
              Commencez à gérer vos procès-verbaux
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              <div>
                <Label htmlFor="name">Nom du club / association</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="border-green-200 focus:border-green-400"
                  placeholder="Ex: Tennis Club Exemple"
                />
              </div>

              <div>
                <Label htmlFor="username">Nom d'utilisateur</Label>
                <div className="relative">
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className="border-green-200 focus:border-green-400 pr-10"
                    placeholder="Ex: TCExemple"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {usernameChecking && (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    )}
                    {!usernameChecking && usernameStatus === 'available' && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {!usernameChecking && usernameStatus === 'taken' && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Au moins 3 caractères, sera utilisé pour la connexion
                </p>
              </div>

              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="border-green-200 focus:border-green-400"
                  placeholder="Au moins 6 caractères"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="border-green-200 focus:border-green-400"
                  placeholder="Confirmez votre mot de passe"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading || usernameStatus === 'taken'}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer le club'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-4">
              <div className="text-sm text-gray-600">
                Déjà un compte ?{' '}
                <Link 
                  to="/login" 
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Se connecter
                </Link>
              </div>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;